import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { concat, fromEvent, merge, Observable, of, race, Scheduler} from 'rxjs';
import {
  debounceTime, distinctUntilChanged, filter, map, reduce, switchAll, switchMap, take, timeInterval, windowToggle,
} from 'rxjs/operators';
import {
  failIfValidOrNonExhaustive,
} from '../Utils';

// See unit tests for the expected behavior of these 3 RxJS operators:
export const openWindow =
  <T>(input: {minTimeBetweenGestures: number, scheduler: Scheduler | undefined}) =>
    (source$: Observable<T>) => {

  const {minTimeBetweenGestures, scheduler} = input;
  const opening$ = concat(of('a'), source$.pipe(
    debounceTime(minTimeBetweenGestures, scheduler),
  ));
  return opening$;
};

export const closeWindow =
  <T>(input: {maxTimeBetweenConsecutiveEvents: number, scheduler: Scheduler | undefined}) =>
    (source$: Observable<T>) => {

  const {maxTimeBetweenConsecutiveEvents, scheduler} = input;
  const closing$ = source$.pipe(
    timeInterval(scheduler),
    map(({interval}) => interval <= maxTimeBetweenConsecutiveEvents ? true : false),
    distinctUntilChanged(),
    filter(value => value === true),
  );
  return closing$;
};

interface TakeFirstPointEventInput {
  minTimeBetweenGestures: number;
  maxTimeBetweenConsecutiveEvents: number;
  scheduler?: Scheduler;
}
export const takeFirstMeaningfulEvent =
  <T>(input: TakeFirstPointEventInput) => (source$: Observable<T>) => {

  const {scheduler, minTimeBetweenGestures, maxTimeBetweenConsecutiveEvents} = input;
  const opening$ = source$.pipe(
    openWindow({minTimeBetweenGestures, scheduler}),
  );
  const closing$ = source$.pipe(
    closeWindow({maxTimeBetweenConsecutiveEvents, scheduler}),
  );
  const output$ = source$.pipe(
    windowToggle(opening$, () => closing$),
    map(stream$ => stream$.pipe(take(1))),
    switchAll(),
  );

  return output$;
};

export enum SlideDirection {
  // This means the presentation/carousel is progressing forward
  ToBeginning = 'ToBeginning',
  // This means the presentation/carousel is progressing backward
  ToEnd = 'ToEnd',
}
// Describe the cause of the change in index: whether it is caused by the user
// directly setting the index or clicking the prev/next buttons. This is
// important because when moving between the first and last slide, the "slide
// direction" depend on whether the user makes that motion happen because of
// prev/next button or by directly choosing a slide:
enum CauseOfMotion {
  DirectSet,
  PrevNext,
}

enum UserInputDirection {
  Forward = 'Forward',
  Backward = 'Backward',
}

const determineDirection = (
    prevIndex: number | undefined,
    nextIndex: number | undefined,
    causeOfMotion: CauseOfMotion | undefined,
    numSlides: number,
  ): SlideDirection => {
  const validatedNextIndex = (nextIndex === undefined) ? 0 : nextIndex;
  let output: SlideDirection;
  if (causeOfMotion !== undefined) {
    if (prevIndex === undefined) {
      // Assume the first carousel slide shown is the first one so the first
      // movement will be to advance the carousel to the right i.e sliding to the
      // left.
      output = SlideDirection.ToEnd;
    } else {
      if (prevIndex === 0 && validatedNextIndex === numSlides - 1) {
        if (causeOfMotion === CauseOfMotion.DirectSet) {
          output = SlideDirection.ToEnd;
        } else if (causeOfMotion === CauseOfMotion.PrevNext) {
          output = SlideDirection.ToBeginning;
        } else {
          failIfValidOrNonExhaustive(causeOfMotion, 'Invalid valud for cause of motion ' + causeOfMotion);
          // The following lines will never be executed:
          output = SlideDirection.ToBeginning;
        }
      } else if (prevIndex === numSlides - 1 && validatedNextIndex === 0) {
        if (causeOfMotion === CauseOfMotion.DirectSet) {
          output = SlideDirection.ToBeginning;
        } else if (causeOfMotion === CauseOfMotion.PrevNext) {
          output = SlideDirection.ToEnd;
        } else {
          failIfValidOrNonExhaustive(causeOfMotion, 'Invalid valud for cause of motion ' + causeOfMotion);
          // The following lines will never be executed:
          output = SlideDirection.ToBeginning;
        }
      } else {
        output = (prevIndex < validatedNextIndex) ? SlideDirection.ToEnd : SlideDirection.ToBeginning;
      }
    }
  } else {
    // Assume motion is forward if for this function is called before
    // prev/next/setIndex is ever called.
    output = SlideDirection.ToEnd;
  }
  return output;
};

type IndexUpdater = (stateUpdater: (value: number) => number) => void;

type IInput = {
  numSlides: number;
  allowWrapAround: boolean
} & (
  {
    allowExternalIndexControl: false,
    initialSlideIndex: number;
  } | {
    allowExternalIndexControl: true
    externalIndex: number
    setExternalIndex: IndexUpdater,
  }
);
export default (input: IInput) => {
  const {
    numSlides,
    allowWrapAround,
  } = input;

  const initialInternalIndex = (input.allowExternalIndexControl === true) ? 0 : input.initialSlideIndex;
  const [internalIndex, setInternalIndex] = useState<number>(initialInternalIndex);
  const index = (input.allowExternalIndexControl === true) ? input.externalIndex : internalIndex;
  const setIndex: IndexUpdater = (input.allowExternalIndexControl === true) ? input.setExternalIndex : setInternalIndex;

  const currentIndexRef = useRef<number | undefined>(undefined);
  const prevIndexRef = useRef<number | undefined>(undefined);
  const causeOfMotionRef = useRef<CauseOfMotion | undefined>(undefined);

  useEffect(() => {
    const newNextIndex = index;
    const newPrevIndex = currentIndexRef.current;
    const oldNextIndex = currentIndexRef.current;
    const oldPrevIndex = prevIndexRef.current;
    if ((oldPrevIndex === undefined && newPrevIndex !== undefined && newPrevIndex !== newNextIndex) ||
          oldNextIndex === undefined && newNextIndex !== undefined ||
          (oldNextIndex !== undefined && oldPrevIndex !== undefined &&
            newNextIndex !== oldNextIndex && newPrevIndex !== oldPrevIndex &&
            newPrevIndex !== newNextIndex)) {

      prevIndexRef.current = currentIndexRef.current;
      currentIndexRef.current = index;
    }
  });

  const getSlideDirection = () => determineDirection(
    prevIndexRef.current,
    currentIndexRef.current,
    causeOfMotionRef.current,
    numSlides,
  );

  const next = () => setIndex(prevValue => {
    causeOfMotionRef.current = CauseOfMotion.PrevNext;
    const nextValue = prevValue + 1;
    let out: number;
    if (nextValue === numSlides) {
      out = (allowWrapAround === true) ? 0 : prevValue;
    } else {
      out = nextValue;
    }
    return out;
  });
  const prev = () => setIndex(prevValue => {

    causeOfMotionRef.current = CauseOfMotion.PrevNext;
    const nextValue = prevValue - 1;
    let out: number;
    if (nextValue < 0) {
      return (allowWrapAround) ? (numSlides - 1) : 0;
    } else {
      out = nextValue;
    }
    return out;
  });
  const onUserSetIndex = (value: number) => {
    causeOfMotionRef.current = CauseOfMotion.DirectSet;
    setIndex(() => value);
  };
  const prevRef = useRef(prev);
  const nextRef = useRef(next);

  useEffect(() => {
    prevRef.current = prev;
    nextRef.current = next;
  });

  useEffect(() => {
    const directionFromWheel$ = fromEvent<WheelEvent>(window, 'wheel').pipe(
      takeFirstMeaningfulEvent({maxTimeBetweenConsecutiveEvents: 100, minTimeBetweenGestures: 200}),
      map(({deltaY}) => {
        if (deltaY >= 0) {
          return UserInputDirection.Forward;
        } else {
          return UserInputDirection.Backward;
        }
      }),
    );
    interface TouchDisplacement {
      deltaY: number;
      pageY: number;
    }
    type TouchReducerAccumulator = undefined | TouchDisplacement;

    // User-defined typeguard to be used in RxJS's filter function:
    const accumulatorIsNotUndefined =
      (arg: TouchReducerAccumulator): arg is TouchDisplacement => arg !== undefined;

    const touchStart$ = fromEvent<TouchEvent>(window, 'touchstart');
    const touchMove$ = fromEvent<TouchEvent>(window, 'touchmove');
    const directionFromTouch$ = touchStart$.pipe(
      switchMap(() => touchMove$.pipe(
        take(2),
        map<TouchEvent, Touch[]>(({touches}) => {
          const touchesLength = touches.length;
          const out: Touch[] = [];
          for (let i = 0; i < touchesLength; i += 1) {
            const touch = touches[i];
            out.push(touch);
          }
          return out;
        }),
        reduce<Touch[], TouchReducerAccumulator>((currentAccumulator, nextTouches) => {
          const {pageY: nextPageY} = nextTouches[0];
          let nextAccumulator: TouchReducerAccumulator;
          if (currentAccumulator === undefined) {
            nextAccumulator = { deltaY: 0, pageY: nextPageY };
          } else {
            const {pageY: prevPageY} = currentAccumulator;
            nextAccumulator = { deltaY: nextPageY - prevPageY, pageY: nextPageY };
          }
          return nextAccumulator;
        }, undefined),
        filter<TouchReducerAccumulator, TouchDisplacement>(accumulatorIsNotUndefined),
        map<TouchDisplacement, UserInputDirection>(({deltaY}) => {
          return deltaY > 0 ? UserInputDirection.Backward : UserInputDirection.Forward;
        }),
      )),
    );

    const directionFromWheelOrTouch$ = race(directionFromWheel$, directionFromTouch$);
    // Remove the 2 lines below when it's time to re-enable scrolling navigation:
    /* tslint:disable-next-line:no-unused-expression */
    directionFromWheelOrTouch$;

    const arrowLeftKeyCode = 37;
    const arrowUpKeyCode = 38;
    const arrowRightKeyCode = 39;
    const arrowDownKeyCode = 40;
    const directionFromKeyboard$ = fromEvent<KeyboardEvent>(document.body, 'keydown').pipe(
      filter(
        ({keyCode}) => (keyCode === arrowLeftKeyCode || keyCode === arrowUpKeyCode ||
                        keyCode === arrowRightKeyCode || keyCode === arrowDownKeyCode),
      ),
      map<KeyboardEvent, UserInputDirection>(({keyCode}) => {
        if (keyCode === arrowLeftKeyCode || keyCode === arrowUpKeyCode) {
          return UserInputDirection.Backward;
        } else {
          return UserInputDirection.Forward;
        }
      }),
    );

    // const direction$ = merge(directionFromWheelOrTouch$, directionFromKeyboard$);
    // Replace the line below with the line above when it's time to re-enable scrolling navigation:
    const direction$ = merge(directionFromKeyboard$);

    const subscription = direction$.subscribe(value => {
      if (value === UserInputDirection.Forward) {
        nextRef.current();
      } else if (value === UserInputDirection.Backward) {
        prevRef.current();
      } else {
        failIfValidOrNonExhaustive(value, 'Invalid user direction input');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    index,
    getSlideDirection,
    setIndex: onUserSetIndex,
    prev,
    next,
  };

};
